import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { ChevronLeft, Flag, HelpCircle, X } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';
import { getWorldExplorer } from '../components/worldExplorer';
import { miniAppRegistry } from '../miniapps';
import {
  getGuidedLesson,
  hasGuidedLesson,
  guidedActivityTypeToAppKey,
  estimatedMinutesFromLesson,
} from '../utils/guidedLessonRegistry';
import { fetchLesson } from '../utils/lessonLoader';
import { getNextLessonInSeries } from '../utils/skillGraph';
import { isLessonAccessible, PREMIUM_PARENT_COPY } from '../config/accessConfig';
import { usePremium } from '../utils/premiumStore';
import {
  loadProgress,
  makeLessonKey,
  markLessonComplete,
  markLessonStarted,
  saveGuidedLessonProgress,
  clearGuidedLessonProgress,
  clearLessonCompletionForReplay,
} from '../utils/progressStore';
import {
  trackEvent,
  trackMiniAppEvent,
  trackLearning,
  setMiniAppAnalyticsContext,
  clearMiniAppAnalyticsContext,
  trackStoryBridgeShown,
  trackStoryBridgeTapped,
} from '../utils/analytics';
import { getCharacter } from '../utils/characters';
import LessonStepNarration from '../components/lesson/LessonStepNarration';
import FocusedLessonShell from '../components/lesson/FocusedLessonShell';
import ReportModal from '../components/feedback/ReportModal';
import QuickFeedback from '../components/feedback/QuickFeedback';
import LessonActivityCard from '../components/lesson/LessonActivityCard';
import LessonCompletionScreen from '../components/lesson/LessonCompletionScreen';
import { getStepNarrationAudio, getStepReadableInstruction } from '../utils/lessonAudio';
import { useLessonNarrationAutoplay } from '../utils/lessonNarrationPreferencesStore';
import { normalizeGuidedHints } from '../miniapps/engines/shared/guidedHintModel';
import { getShellMechanicHint } from '../utils/guidedLessonMechanicHints';
import { useMaxWidth } from '../utils/useMaxWidth';
import {
  pickPrimaryVisiblePrompt,
  suggestedTapRoundStepHeadline,
} from '../miniapps/engines/shared/guidedTapRoundUi';

const pageStyle = {
  minHeight: '100vh',
  background: 'var(--color-bg)',
  padding: '20px 18px 48px',
  maxWidth: 720,
  margin: '0 auto',
  boxSizing: 'border-box',
};

const WHY_FALLBACK =
  'This step is helping you understand the idea by trying it yourself.';

function isInteractiveStep(step) {
  return step?.type === 'activity' || step?.type === 'miniChallenge';
}

function splitInstructionParagraphs(text) {
  if (typeof text !== 'string') return { lead: '', rest: '' };
  const parts = text.split(/\n\n+/).map((p) => p.trim()).filter(Boolean);
  if (parts.length === 0) return { lead: '', rest: '' };
  if (parts.length === 1) return { lead: parts[0], rest: '' };
  return { lead: parts[0], rest: parts.slice(1).join('\n\n') };
}

/**
 * Read phase + step from localStorage synchronously so the first paint matches saved progress
 * (avoids flashing step 1 before useEffect restores a later step).
 */
function readLessonBootstrap(worldId, lessonId) {
  const lesson = worldId && lessonId ? getGuidedLesson(worldId, lessonId) : null;
  const key = worldId && lessonId ? makeLessonKey(worldId, lessonId) : '';
  if (!lesson || !key) {
    return { phase: 'lesson', stepIndex: 0, activityStepsDone: {} };
  }
  const snap = loadProgress();
  if (snap.completedLessons.includes(key)) {
    return { phase: 'done', stepIndex: 0, activityStepsDone: {} };
  }
  const p = snap.guidedLessonProgress[key];
  let stepIndex = 0;
  let activityStepsDone = {};
  if (p && typeof p.stepIndex === 'number' && lesson.sequence?.length) {
    stepIndex = Math.min(p.stepIndex, lesson.sequence.length - 1);
    activityStepsDone =
      p.activityStepsDone && typeof p.activityStepsDone === 'object' ? { ...p.activityStepsDone } : {};
  }
  return { phase: 'lesson', stepIndex, activityStepsDone };
}

export default function LessonPage() {
  const { worldId, lessonId, locale } = useParams();
  const navigate = useNavigate();

  const topicsBase = locale ? `/${locale}/topics` : '/topics';
  const backToWorld = `${topicsBase}/${worldId}`;

  const world = worldId ? getWorldExplorer(worldId) : null;
  const lessonKey = worldId && lessonId ? makeLessonKey(worldId, lessonId) : '';
  // Derived world title — falls back to worldId when pebbles/story worlds aren't in worldExplorerData yet
  // (resolved after lesson loads so dynamic lessons can supply their worldId)
  const worldTitle = world?.title ?? (worldId ? String(worldId) : '');

  const [phase, setPhase] = useState(() => readLessonBootstrap(worldId, lessonId).phase);
  const [stepIndex, setStepIndex] = useState(() => readLessonBootstrap(worldId, lessonId).stepIndex);
  const [activityStepsDone, setActivityStepsDone] = useState(
    () => readLessonBootstrap(worldId, lessonId).activityStepsDone,
  );

  const prevLessonKeyRef = useRef(null);
  const lessonActivityTimerRef = useRef(null);
  const activityAdvanceGenRef = useRef(0);
  const lessonAudioRef = useRef(null);
  const storyBridgeFiredRef = useRef(false);
  const storyBridgeTappedRef = useRef(false);
  // abandonRef is written every render and read only in the unmount cleanup
  const abandonRef = useRef(null);
  const [lessonFooterActions, setLessonFooterActions] = useState(null);
  const [activityFooterWrongHint, setActivityFooterWrongHint] = useState(false);
  /** null | 'hint' | 'why' — floating panel above bottom bar */
  const [lessonPanel, setLessonPanel] = useState(null);
  const [hintVisibleLevel, setHintVisibleLevel] = useState(1);
  const [reportOpen, setReportOpen] = useState(false);

  const [premiumActive] = usePremium();
  const [narrationAutoplay] = useLessonNarrationAutoplay();
  /** Phone-sized lesson layout: tighter chrome + stacked footer (primary CTA above clue row). */
  const lessonNarrow = useMaxWidth(520);
  /** `?tapLesson` — desktop preview of tap-first / no-scroll lesson UI (see FocusedLessonShell scrollMode). */
  const [searchParams] = useSearchParams();
  const tapLessonPreview = searchParams.has('tapLesson');
  const tapFirst = lessonNarrow || tapLessonPreview;
  const accessible =
    worldId && lessonId
      ? isLessonAccessible(worldId, lessonId, { hasPremium: premiumActive })
      : false;

  // Optional deep-link URL (e.g. Kids Place passes ?lessonUrl=<full-url>)
  const lessonUrl = searchParams.get('lessonUrl');
  // Sync value from the static bundle — instant for all pre-imported lessons
  const staticLesson = worldId && lessonId ? getGuidedLesson(worldId, lessonId) : null;
  // Async overlay: populated when lessonUrl is provided or lessonId is not in the static bundle
  const [dynamicLesson, setDynamicLesson] = useState(null);
  const [lessonLoading, setLessonLoading] = useState(false);
  // lessonUrl fetch overrides static bundle; dynamic supplements static as fallback
  const lesson = lessonUrl ? dynamicLesson : (dynamicLesson ?? staticLesson);

  // Story deep-link params (Kids Place integration — present when ?from=story)
  const fromStory = searchParams.get('from') === 'story';
  const storyId = searchParams.get('storyId') ?? '';
  // Validate ?character= against the known protagonist roster; render without
  // character framing when the name is unrecognised (forward-compat, no crash).
  const storyCharacter = (() => {
    const raw = searchParams.get('character') ?? '';
    if (!raw) return '';
    if (!getCharacter(raw)) {
      console.warn(`[LessonPage] Unknown ?character="${raw}" — rendering without character framing.`);
      return '';
    }
    return raw;
  })();

  /** Next lesson in path (for completion screen primary CTA). */
  const completionNextLesson = useMemo(() => {
    if (!worldId || !lessonId) return { path: null, title: null };
    const sn = getNextLessonInSeries(worldId, lessonId);
    const nid = sn?.nextLessonId;
    if (!nid || !hasGuidedLesson(worldId, nid)) return { path: null, title: null };
    if (!isLessonAccessible(worldId, nid, { hasPremium: premiumActive })) return { path: null, title: null };
    const path = locale
      ? `/${locale}/topics/${worldId}/lesson/${nid}`
      : `/topics/${worldId}/lesson/${nid}`;
    const nextLes = getGuidedLesson(worldId, nid);
    const title = typeof nextLes?.title === 'string' ? nextLes.title.trim() : null;
    return { path, title };
  }, [worldId, lessonId, premiumActive, locale]);

  const step = lesson?.sequence?.[stepIndex];

  // Keep abandonment snapshot current — read only in the unmount cleanup below
  abandonRef.current = {
    phase,
    worldId,
    lessonId,
    lessonTitle: lesson?.title ?? '',
    totalSteps: lesson?.sequence?.length ?? 0,
    stepIndex,
    step,
    fromStory,
    storyCharacter,
  };

  const lessonHintsBundle = useMemo(() => {
    const payload =
      step && isInteractiveStep(step) && step.activityData && typeof step.activityData === 'object'
        ? step.activityData
        : {};
    const raw = Array.isArray(payload.hints) ? payload.hints : [];
    const shell = raw.length > 0 ? normalizeGuidedHints(raw) : [];
    return { activityPayload: payload, rawActivityHints: raw, shellNormalizedHints: shell };
  }, [step]);

  const { activityPayload, rawActivityHints, shellNormalizedHints } = lessonHintsBundle;

  const displayHints = useMemo(() => {
    if (!step || !isInteractiveStep(step)) return [];
    const mechanic = getShellMechanicHint(step.activityType);
    const out = [];
    if (mechanic?.text) {
      out.push({
        text: mechanic.text,
        audioText: mechanic.audioText || mechanic.text,
        level: 1,
      });
    }
    let lvl = out.length;
    for (const h of shellNormalizedHints) {
      lvl += 1;
      out.push({ ...h, level: lvl });
    }
    return out;
  }, [step, shellNormalizedHints]);

  const hintMax = displayHints.length;

  useEffect(() => {
    let cancelled = false;
    setDynamicLesson(null);

    const needsLoad = !!(lessonUrl || (lessonId && !staticLesson));
    setLessonLoading(needsLoad);

    if (!needsLoad) return undefined;

    async function load() {
      let loaded = null;
      try {
        if (lessonUrl) {
          const res = await fetch(lessonUrl);
          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          loaded = await res.json();
        } else {
          loaded = await fetchLesson(lessonId);
        }
      } catch (err) {
        console.warn('[LessonPage] lesson load failed:', err);
      }
      if (!cancelled) {
        if (loaded) setDynamicLesson(loaded);
        setLessonLoading(false);
      }
    }

    load();
    return () => { cancelled = true; };
    // staticLesson is derived from worldId+lessonId which are already deps; including it
    // avoids the exhaustive-deps warning without changing when the effect runs.
  }, [worldId, lessonId, lessonUrl, staticLesson]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'lesson') return undefined;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [phase]);

  useEffect(() => {
    if (lessonKey && lesson) markLessonStarted(lessonKey);
  }, [lessonKey, lesson]);

  // Auto-navigate back to story 2 seconds after lesson completion in story mode.
  useEffect(() => {
    if (phase !== 'done' || !fromStory || !storyId) return undefined;
    const characterForNav = (searchParams.get('character') ?? '').toLowerCase();
    if (!characterForNav) return undefined;
    const returnUrl =
      `/stories/${characterForNav}/${storyId}` +
      `?returned=true&lessonId=${encodeURIComponent(lessonId)}`;
    const t = setTimeout(() => navigate(returnUrl, { replace: true }), 2000);
    return () => clearTimeout(t);
  }, [phase, fromStory, storyId, lessonId, navigate, searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (worldId && lessonId && lessonKey && lesson) {
      trackLearning.lessonOpen(worldId, lessonId, lesson.title, lesson.sequence?.length ?? 0);
    }
  }, [worldId, lessonId, lessonKey, lesson]);

  // Fire story_bridge_shown once when this lesson is first rendered in story mode
  useEffect(() => {
    if (!fromStory || !lesson || storyBridgeFiredRef.current) return;
    storyBridgeFiredRef.current = true;
    trackStoryBridgeShown({ storyId, character: storyCharacter, lessonId });
  }, [fromStory, lesson, storyId, storyCharacter, lessonId]);

  // Fire lesson_abandoned when component unmounts while lesson is still in progress
  useEffect(() => {
    return () => {
      const s = abandonRef.current;
      if (s?.phase === 'lesson' && s.worldId && s.lessonId) {
        trackLearning.lessonAbandoned(
          s.worldId,
          s.lessonId,
          s.lessonTitle,
          s.stepIndex,
          s.totalSteps,
          {
            last_step_index: s.stepIndex,
            last_step_type: s.step?.type ?? null,
            from_story: s.fromStory,
            character: s.storyCharacter || null,
          },
        );
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (phase !== 'lesson' || !worldId || !lessonId || !lesson) return;
    const s = lesson.sequence?.[stepIndex];
    if (!s) return;
    trackLearning.lessonStepView(
      worldId,
      lessonId,
      lesson.title,
      stepIndex,
      lesson.sequence.length,
      s.type,
      s.stepId,
    );
  }, [phase, worldId, lessonId, lesson, stepIndex]);

  useLayoutEffect(() => {
    if (!lessonKey || !lesson) return;
    if (prevLessonKeyRef.current === lessonKey) return;
    if (lessonActivityTimerRef.current != null) {
      clearTimeout(lessonActivityTimerRef.current);
      lessonActivityTimerRef.current = null;
    }
    activityAdvanceGenRef.current += 1;
    prevLessonKeyRef.current = lessonKey;
    const b = readLessonBootstrap(worldId, lessonId);
    setPhase(b.phase);
    setStepIndex(b.stepIndex);
    setActivityStepsDone(b.activityStepsDone);
  }, [lessonKey, lesson, worldId, lessonId]);

  /** Keep stepIndex in range when sequence length changes (HMR, edits) or storage was stale — avoids empty lesson UI. */
  useLayoutEffect(() => {
    if (phase !== 'lesson' || !lesson?.sequence?.length) return;
    const max = lesson.sequence.length - 1;
    if (stepIndex > max || stepIndex < 0) {
      setStepIndex(Math.min(Math.max(stepIndex, 0), max));
    }
  }, [phase, lesson, stepIndex]);

  useEffect(() => {
    if (!accessible && worldId && lessonId) {
      navigate(backToWorld, { replace: true });
    }
  }, [accessible, worldId, lessonId, navigate, backToWorld]);

  useEffect(() => {
    if (phase !== 'lesson' || !lessonKey || !lesson) return;
    saveGuidedLessonProgress(lessonKey, { stepIndex, activityStepsDone });
  }, [phase, stepIndex, activityStepsDone, lessonKey, lesson]);

  useEffect(() => {
    setLessonFooterActions(null);
    setActivityFooterWrongHint(false);
    setLessonPanel(null);
    setHintVisibleLevel(1);
  }, [stepIndex, step?.stepId]);

  useEffect(() => {
    if (lessonPanel == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLessonPanel(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lessonPanel]);

  const totalSteps = lesson?.sequence?.length ?? 0;
  const stepNarration = step ? getStepNarrationAudio(step) : null;
  const narrationSrc = stepNarration?.src ?? null;

  const interactiveComplete =
    step && isInteractiveStep(step) ? !!activityStepsDone[step.stepId] : true;

  const canGoNext = useMemo(() => {
    if (!step) return false;
    if (isInteractiveStep(step)) return interactiveComplete;
    return true;
  }, [step, interactiveComplete]);

  const clearLessonActivityTimer = useCallback(() => {
    if (lessonActivityTimerRef.current != null) {
      clearTimeout(lessonActivityTimerRef.current);
      lessonActivityTimerRef.current = null;
    }
  }, []);

  const handleNext = useCallback(() => {
    clearLessonActivityTimer();
    if (!lesson || !step) return;
    if (!canGoNext) return;
    // First Continue tap in story mode = child engaged with the bridge lesson
    if (fromStory && stepIndex === 0 && !storyBridgeTappedRef.current) {
      storyBridgeTappedRef.current = true;
      trackStoryBridgeTapped({ storyId, character: storyCharacter, lessonId });
    }
    if (step.type === 'recap') {
      markLessonComplete(lessonKey, {
        guidedLesson: true,
        estimatedMinutes: lesson.estimatedMinutes,
        steps: lesson.sequence.length,
      });
      if (worldId && lessonId) {
        trackLearning.lessonComplete(worldId, lessonId, lesson.title, lesson.sequence.length);
      }
      clearGuidedLessonProgress(lessonKey);
      setPhase('done');
      return;
    }
    if (stepIndex < totalSteps - 1) {
      setStepIndex((i) => i + 1);
    }
  }, [
    lesson,
    step,
    canGoNext,
    stepIndex,
    totalSteps,
    lessonKey,
    worldId,
    lessonId,
    fromStory,
    storyId,
    storyCharacter,
    clearLessonActivityTimer,
  ]);

  const handleActivityComplete = useCallback(() => {
    if (!step) return;
    if (activityStepsDone[step.stepId]) return;
    clearLessonActivityTimer();
    activityAdvanceGenRef.current += 1;
    setActivityStepsDone((prev) => ({ ...prev, [step.stepId]: true }));
    trackMiniAppEvent.interaction(String(step.stepId), 'activity_done');
    setLessonFooterActions(null);
    setLessonPanel(null);
    setHintVisibleLevel(1);
  }, [step, activityStepsDone, clearLessonActivityTimer]);

  const registerLessonFooterActions = useCallback((actions) => {
    setLessonFooterActions(actions && typeof actions === 'object' ? actions : null);
  }, []);

  const handleEngineProgress = useCallback((detail) => {
    if (detail?.engine !== 'sequence') return;
    if (detail.phase === 'check') {
      if (detail.lastCorrect === false) setActivityFooterWrongHint(true);
      if (detail.lastCorrect === true) setActivityFooterWrongHint(false);
    }
    if (detail.phase === 'reorder' || detail.phase === 'reset') {
      setActivityFooterWrongHint(false);
    }
  }, []);

  useEffect(() => {
    if (interactiveComplete && step && isInteractiveStep(step)) {
      setLessonFooterActions(null);
      setActivityFooterWrongHint(false);
    }
  }, [interactiveComplete, step]);

  useEffect(() => {
    return () => clearLessonActivityTimer();
  }, [stepIndex, lessonKey, clearLessonActivityTimer]);

  useEffect(() => {
    const el = lessonAudioRef.current;
    if (!el) return;
    el.pause();
    el.currentTime = 0;
    if (!narrationSrc) {
      el.removeAttribute('src');
      try {
        el.load();
      } catch {
        /* ignore */
      }
    }
  }, [stepIndex, step?.stepId, lessonKey, narrationSrc]);

  useEffect(() => {
    return () => {
      const el = lessonAudioRef.current;
      if (!el) return;
      el.pause();
      el.removeAttribute('src');
      try {
        el.load();
      } catch {
        /* ignore */
      }
    };
  }, []);

  const handlePlayAgain = useCallback(() => {
    trackEvent('lesson_replayed', 'Learning', lessonId, {
      world_id: worldId,
      lesson_id: lessonId,
      from_story: fromStory,
    });
    clearLessonActivityTimer();
    activityAdvanceGenRef.current += 1;
    clearLessonCompletionForReplay(lessonKey);
    setActivityStepsDone({});
    setStepIndex(0);
    setPhase('lesson');
  }, [lessonKey, worldId, lessonId, fromStory, clearLessonActivityTimer]);

  const minutes = estimatedMinutesFromLesson(lesson);
  const readableInstruction = step ? getStepReadableInstruction(step) : '';
  const instructionParts = step ? splitInstructionParagraphs(readableInstruction) : { lead: '', rest: '' };
  const displayStepTitle = useMemo(() => {
    const raw = typeof step?.title === 'string' ? step.title : '';
    if (!step || !tapFirst || !isInteractiveStep(step)) return raw;
    const content =
      activityPayload?.content && typeof activityPayload.content === 'object' ? activityPayload.content : {};
    const sug = suggestedTapRoundStepHeadline({
      activityType: step.activityType,
      stepTitle: raw,
      activityTitle: activityPayload?.title,
      content,
    });
    return sug || raw;
  }, [step, tapFirst, activityPayload]);
  const iconPair =
    step && Array.isArray(step.iconPair) && step.iconPair.length >= 2
      ? [String(step.iconPair[0]), String(step.iconPair[1])]
      : null;

  const appKey =
    step && isInteractiveStep(step) ? guidedActivityTypeToAppKey(step.activityType) : null;
  const Engine = appKey ? miniAppRegistry[appKey] : null;

  useEffect(() => {
    if (!worldId || !lessonId || !step?.stepId || !appKey) {
      clearMiniAppAnalyticsContext();
      return undefined;
    }
    setMiniAppAnalyticsContext({
      world_id: worldId,
      lesson_id: lessonId,
      activity_type: appKey,
      step_id: step.stepId,
    });
    return clearMiniAppAnalyticsContext;
  }, [worldId, lessonId, step?.stepId, appKey]);

  const shouldShowInstruction = useMemo(() => {
    if (!step?.instruction?.trim()) return false;

    if (appKey === 'engine_story_choice') return false;

    const content = step?.activityData?.content;
    if (!content) return true;

    const checkFields = [
      content.situation,
      content.question,
      content.instructions,
      content.description,
      content.prompt,
      content.title,
    ].filter((text) => typeof text === 'string' && text.trim().length > 0);

    if (checkFields.length === 0) return true;

    const instructionWords = new Set(
      step.instruction
        .toLowerCase()
        .split(/\W+/)
        .filter((w) => w.length > 2),
    );

    for (const field of checkFields) {
      const fieldWords = field
        .toLowerCase()
        .split(/\W+/)
        .filter((w) => w.length > 2);

      const matchCount = fieldWords.filter((w) => instructionWords.has(w)).length;

      const overlap = instructionWords.size > 0 ? matchCount / instructionWords.size : 0;

      if (overlap >= 0.65) {
        console.debug(
          '[LessonPage] suppressing duplicate instruction for step',
          step.stepId,
          'overlap:',
          Math.round(overlap * 100) + '%',
        );
        return false;
      }
    }

    return true;
  }, [step, appKey]);

  const isLessonMcEngine = appKey === 'engine_multiple_choice';
  const isLessonMatchEngine = appKey === 'engine_match';
  const isLessonCanvasEngine = appKey === 'engine_canvas_manipulative';
  const isLessonPatternEngine = appKey === 'engine_pattern';
  const isLessonGearEngine =
    appKey === 'engine_gear_ratio' ||
    appKey === 'engine_gear_direction' ||
    appKey === 'engine_gear_speed';
  const isLessonCircuitEngine = appKey === 'engine_circuit_builder';
  const isLessonCircuitPathEngine = appKey === 'engine_circuit_path';
  const isLessonSwitchLogicEngine = appKey === 'engine_switch_logic';
  const isLessonVariableSimulationEngine = appKey === 'engine_variable_simulation';
  const isLessonSimulationScrubEngine = appKey === 'engine_simulation_scrub';
  const isLessonSequenceEngine = appKey === 'engine_sequence';
  const isLessonSystemFlowEngine = appKey === 'engine_system_flow';
  const isLessonEvidenceDetectiveEngine = appKey === 'engine_evidence_detective';
  const isLessonStoryChoiceEngine = appKey === 'engine_story_choice';
  const isLessonTapSurfaceEngine =
    isLessonMcEngine ||
    isLessonGearEngine ||
    isLessonCircuitEngine ||
    isLessonCircuitPathEngine ||
    isLessonSwitchLogicEngine;
  const suppressFooterSuccessStrip = isLessonMcEngine || isLessonMatchEngine || isLessonPatternEngine;

  const stepExplanation =
    typeof step?.explanation === 'string' && step.explanation.trim() ? step.explanation.trim() : '';
  const payloadExplanation =
    typeof activityPayload.explanation === 'string' && activityPayload.explanation.trim()
      ? activityPayload.explanation.trim()
      : '';
  const whyText = stepExplanation || payloadExplanation || WHY_FALLBACK;

  const guidedHintsEnabled = rawActivityHints.length === 0;

  const hintsForMcEngine = useMemo(() => {
    if (!isLessonMcEngine) return rawActivityHints;
    const lead = (instructionParts.lead || '').trim();
    if (!lead) return rawActivityHints;
    const prefix = lead.slice(0, Math.min(48, lead.length));
    const dup =
      Array.isArray(rawActivityHints) &&
      rawActivityHints.some((h) => {
        const t = typeof h === 'string' ? h : h?.text;
        return typeof t === 'string' && (t.trim() === lead || t.trim().startsWith(prefix));
      });
    if (dup) return rawActivityHints;
    return [lead, ...rawActivityHints];
  }, [isLessonMcEngine, instructionParts.lead, rawActivityHints]);

  const engineProps =
    step && isInteractiveStep(step)
      ? {
          title: activityPayload.title,
          content: activityPayload.content || {},
          hints: hintsForMcEngine,
          explanation:
            typeof activityPayload.explanation === 'string' ? activityPayload.explanation : undefined,
          activityId: step.stepId,
          onComplete: handleActivityComplete,
          onProgress: handleEngineProgress,
          lessonUiMode: tapFirst || isLessonTapSurfaceEngine ? 'tapOnly' : 'default',
          onRegisterLessonFooterActions: registerLessonFooterActions,
          guidedHintsEnabled,
          guidedContext: {
            singleSurface: true,
            stepTitle: typeof step.title === 'string' ? step.title : '',
            stepInstructionLead: instructionParts.lead || '',
            stepInstructionRest: instructionParts.rest || '',
            readableInstruction: typeof readableInstruction === 'string' ? readableInstruction : '',
          },
        }
      : null;

  const isInteractiveStepActive = !!(step && isInteractiveStep(step));
  const immersiveHeader = isInteractiveStepActive && !interactiveComplete;
  const shellHeaderMinimal = immersiveHeader || (lessonNarrow && isInteractiveStepActive);

  const lessonMainCardStyle = {
    borderRadius: lessonNarrow && immersiveHeader ? 14 : 18,
    padding: (() => {
      if (!isInteractiveStepActive) return lessonNarrow ? 18 : 22;
      if (immersiveHeader && lessonNarrow && isLessonMcEngine) return '8px 10px 10px';
      if (immersiveHeader && lessonNarrow && isLessonVariableSimulationEngine) return '8px 10px 10px';
      if (immersiveHeader && lessonNarrow && isLessonSequenceEngine) return '8px 10px 10px';
      if (immersiveHeader && lessonNarrow && isLessonSystemFlowEngine) return '8px 10px 10px';
      if (immersiveHeader && lessonNarrow && isLessonEvidenceDetectiveEngine) return '8px 10px 10px';
      if (immersiveHeader && lessonNarrow) return '10px 10px 12px';
      return '14px 14px 16px';
    })(),
    background: 'var(--color-surface, var(--color-bg-card))',
    border: '1px solid var(--color-border)',
    boxShadow: immersiveHeader ? 'none' : '0 6px 24px var(--color-shadow)',
    marginBottom: 0,
    flex: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
  };
  const leadSize = 15;
  const restSize = 14;
  const engineTop = lessonNarrow && immersiveHeader ? 8 : 14;
  const titleSize = immersiveHeader
    ? 'clamp(0.98rem, 2.8vw, 1.12rem)'
    : 'clamp(1.12rem, 3.5vw, 1.35rem)';

  const showLessonNext = !step || !isInteractiveStep(step) || interactiveComplete;

  const wrongFooterCopy =
    step?.failureText?.trim() ||
    (tapFirst
      ? 'Almost! Use the arrows to put the steps in a better order.'
      : 'Almost! Try moving the steps into a better order.');

  const successFooterCopy =
    step?.successText?.trim() ||
    'Great job! Ready for the next step.';

  const goalCalloutStyle = {
    padding: immersiveHeader && lessonNarrow ? '8px 10px' : '10px 12px',
    marginBottom: lessonNarrow && immersiveHeader ? 8 : 12,
    borderRadius: 14,
    borderLeft: immersiveHeader ? '3px solid var(--color-border)' : '4px solid var(--color-primary)',
    background: immersiveHeader ? 'var(--color-bg)' : 'rgba(139, 92, 246, 0.08)',
    borderTop: '1px solid var(--color-border)',
    borderRight: '1px solid var(--color-border)',
    borderBottom: '1px solid var(--color-border)',
  };
  const goalLabelStyle = {
    fontSize: 11,
    fontWeight: 800,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    color: immersiveHeader ? 'var(--color-text-soft, var(--color-text))' : 'var(--color-primary)',
    marginBottom: 6,
    opacity: immersiveHeader ? 0.75 : 1,
  };
  const goalBodyStyle = {
    margin: 0,
    lineHeight: 1.45,
    fontSize: leadSize,
    fontWeight: 600,
    color: 'var(--color-text)',
    opacity: 0.94,
  };

  const hideTapRoundShellTitle =
    tapFirst &&
    (isLessonMatchEngine || isLessonCanvasEngine) &&
    isInteractiveStepActive &&
    !interactiveComplete;

  const instructionLeadForDisplay = useMemo(() => {
    const lead = (instructionParts.lead || '').trim();
    if (!lead) return '';
    if (!tapFirst || !step || !isInteractiveStep(step) || (!isLessonMatchEngine && !isLessonCanvasEngine)) {
      return lead;
    }
    const { suppressed } = pickPrimaryVisiblePrompt([{ text: displayStepTitle }, { text: lead }]);
    if (suppressed.includes(lead)) return '';
    return lead;
  }, [
    instructionParts.lead,
    displayStepTitle,
    tapFirst,
    step,
    isLessonMatchEngine,
    isLessonCanvasEngine,
  ]);

  if (lessonLoading && !lesson) {
    return (
      <div style={pageStyle}>
        <p style={{ color: 'var(--color-text-soft, var(--color-text))', opacity: 0.7 }}>
          Loading lesson…
        </p>
      </div>
    );
  }

  if (!lesson) {
    return (
      <div style={pageStyle}>
        <p style={{ color: 'var(--color-text)' }}>This lesson is not available.</p>
        <Link to={`${topicsBase}/explorer`} style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
          Back to worlds
        </Link>
      </div>
    );
  }

  if (!accessible) {
    return (
      <div style={pageStyle}>
        <p style={{ color: 'var(--color-text)', lineHeight: 1.5, marginBottom: 16 }}>{PREMIUM_PARENT_COPY}</p>
        <Link to={backToWorld} style={{ color: 'var(--color-primary)', fontWeight: 600 }}>
          Back to {world?.title ?? 'topics'}
        </Link>
      </div>
    );
  }

  if (phase === 'done') {
    if (fromStory) {
      return (
        <div
          style={{
            ...pageStyle,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '100vh',
            textAlign: 'center',
            gap: 8,
          }}
        >
          <p style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', margin: 0 }}>
            Lesson complete! ⭐
          </p>
          <p style={{ fontSize: 14, color: 'var(--color-text)', opacity: 0.65, margin: 0 }}>
            Returning to your story…
          </p>
        </div>
      );
    }
    return (
      <LessonCompletionScreen
        lesson={lesson}
        worldTitle={worldTitle}
        backToWorld={backToWorld}
        nextLessonPath={completionNextLesson.path}
        nextLessonTitle={completionNextLesson.title}
        onPlayAgain={handlePlayAgain}
      />
    );
  }

  const characterLine =
    typeof lesson?.storyBridge?.characterLine === 'string'
      ? lesson.storyBridge.characterLine.trim()
      : '';
  const storyCharacterBar =
    fromStory && stepIndex === 0 && (storyCharacter || characterLine) ? (
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          marginBottom: 10,
          padding: '7px 11px',
          borderRadius: 12,
          background: 'rgba(139, 92, 246, 0.07)',
          border: '1px solid rgba(139, 92, 246, 0.15)',
        }}
      >
        {storyCharacter ? (
          <span
            style={{
              fontSize: 10,
              fontWeight: 800,
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
              color: 'var(--color-primary)',
              flexShrink: 0,
            }}
          >
            {storyCharacter}
          </span>
        ) : null}
        {characterLine ? (
          <span
            style={{
              fontSize: 13,
              fontWeight: 500,
              color: 'var(--color-text)',
              lineHeight: 1.4,
              opacity: 0.85,
              fontStyle: 'italic',
            }}
          >
            {storyCharacter ? '— ' : ''}{characterLine}
          </span>
        ) : null}
      </div>
    ) : null;

  const instructionBlock = shouldShowInstruction ? (
    <>
      {instructionLeadForDisplay ? (
        step && isInteractiveStep(step) ? (
          <div style={goalCalloutStyle} role="region" aria-label="Goal for this step">
            {immersiveHeader ? null : <div style={goalLabelStyle}>Goal</div>}
            <p style={{ ...goalBodyStyle, fontWeight: immersiveHeader ? 500 : 600 }}>{instructionLeadForDisplay}</p>
          </div>
        ) : (
          <p style={{ margin: 0, lineHeight: 1.55, fontSize: leadSize, opacity: 0.9 }}>{instructionLeadForDisplay}</p>
        )
      ) : null}
      {instructionParts.rest ? (
        <p
          style={{
            margin: '10px 0 0',
            lineHeight: 1.55,
            fontSize: restSize,
            opacity: 0.78,
            padding: '10px 12px',
            borderRadius: 14,
            background: 'var(--color-bg)',
            border: '1px solid var(--color-border)',
            whiteSpace: 'pre-line',
          }}
        >
          {instructionParts.rest}
        </p>
      ) : null}
    </>
  ) : null;

  /** Tap-first “immersive” steps hide the goal from the card header; show it above the engine instead.
   *  Tap-first canvas guided rounds already carry headline + item + choices; repeating the step instruction is noisy.
   *  Lesson MC is always tap-style in this shell (prompt + choices + footer Check); step instruction duplicates that.
   *  Tap-first match rounds use the same pattern as canvas (in-engine prompts + footer actions). */
  const showImmersiveInstruction =
    immersiveHeader &&
    isInteractiveStepActive &&
    !(isLessonCanvasEngine && tapFirst) &&
    !isLessonMcEngine &&
    !(isLessonMatchEngine && tapFirst) &&
    !(isLessonPatternEngine && tapFirst) &&
    !(isLessonGearEngine && tapFirst) &&
    !(isLessonCircuitEngine && tapFirst) &&
    !(isLessonCircuitPathEngine && tapFirst) &&
    !(isLessonSwitchLogicEngine && tapFirst) &&
    !isLessonSequenceEngine &&
    !isLessonSystemFlowEngine &&
    !isLessonEvidenceDetectiveEngine &&
    !isLessonVariableSimulationEngine &&
    !isLessonSimulationScrubEngine &&
    !isLessonStoryChoiceEngine &&
    (!!instructionLeadForDisplay || !!(instructionParts.rest && String(instructionParts.rest).trim()));

  const narrationInBottomBar = !!(tapFirst && stepNarration && step);
  const narrationBlock =
    stepNarration && step && !narrationInBottomBar ? (
      <LessonStepNarration
        src={stepNarration.src}
        audioRef={lessonAudioRef}
        stepId={step.stepId}
        stepTitle={step.title}
        autoplay={narrationAutoplay}
        dense={immersiveHeader && lessonNarrow}
      />
    ) : null;

  const engineBlock =
    Engine && engineProps ? (
      <div style={{ marginTop: showImmersiveInstruction ? 0 : engineTop, flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
        <Engine key={step.stepId} {...engineProps} />
      </div>
    ) : isInteractiveStep(step) ? (
      <div
        style={{
          marginTop: engineTop,
          padding: 16,
          borderRadius: 14,
          border: '1px dashed var(--color-warn)',
          background: 'rgba(245, 158, 11, 0.12)',
          color: 'var(--color-text)',
          fontSize: 15,
          lineHeight: 1.45,
        }}
        role="status"
      >
        This activity could not be loaded
        {step?.activityType ? ` (type: ${step.activityType})` : ''}
        {appKey ? '' : ' — no mini-app is registered for that type.'}
      </div>
    ) : null;

  const iconBlock = iconPair ? (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: 14,
        marginBottom: 12,
        fontSize: 'clamp(1.75rem, 7vw, 2.35rem)',
        lineHeight: 1,
      }}
      aria-hidden
    >
      <span>{iconPair[0]}</span>
      <span style={{ opacity: 0.45 }}>+</span>
      <span>{iconPair[1]}</span>
    </div>
  ) : null;

  const footerHintLineStyle = {
    margin: '0 0 4px',
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1.35,
    color: 'var(--color-text)',
    opacity: 0.78,
    textAlign: 'center',
  };

  const footerSuccessLineStyle = {
    margin: '0 0 6px',
    fontSize: 13,
    fontWeight: 700,
    lineHeight: 1.35,
    color: 'var(--color-text)',
    textAlign: 'center',
  };

  let primaryLabel = 'Continue';
  let primaryDisabled = false;
  let primaryAction = handleNext;
  if (step && isInteractiveStep(step)) {
    if (interactiveComplete) {
      primaryLabel = 'Continue';
      primaryDisabled = false;
      primaryAction = handleNext;
    } else if (typeof lessonFooterActions?.continueLesson === 'function') {
      primaryLabel = 'Continue';
      primaryDisabled = lessonFooterActions.continueLessonDisabled === true;
      primaryAction = () => lessonFooterActions.continueLesson?.();
    } else if (lessonFooterActions?.checkAnswer) {
      const cl = lessonFooterActions.checkAnswerLabel;
      primaryLabel = typeof cl === 'string' && cl.trim() ? cl.trim() : 'Check';
      primaryDisabled = lessonFooterActions.checkAnswerDisabled === true;
      primaryAction = () => lessonFooterActions.checkAnswer?.();
    } else {
      primaryLabel = 'Continue';
      primaryDisabled = true;
      primaryAction = handleNext;
    }
  }

  const secondaryBarBtn = {
    flex: 1,
    minWidth: 0,
    minHeight: lessonNarrow ? 48 : 40,
    padding: '8px 8px',
    borderRadius: 12,
    border: '1px solid var(--color-border)',
    background: 'var(--color-bg-card)',
    color: 'var(--color-text)',
    fontWeight: 700,
    fontSize: 13,
    cursor: 'pointer',
    boxShadow: '0 1px 6px var(--color-shadow)',
    boxSizing: 'border-box',
  };

  const clueIconBtn = {
    ...secondaryBarBtn,
    flex: '0 0 48px',
    minWidth: 48,
    maxWidth: 48,
    padding: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  };

  const primaryBarBtn = {
    flex: 1.25,
    minWidth: 0,
    minHeight: lessonNarrow ? 52 : 42,
    padding: '10px 12px',
    borderRadius: 12,
    border: primaryDisabled ? '1px solid var(--color-border)' : 'none',
    background: primaryDisabled ? 'var(--color-bg-card)' : 'var(--color-primary)',
    color: primaryDisabled ? 'var(--color-text-soft, var(--color-text))' : '#fff',
    fontWeight: 800,
    fontSize: 14,
    cursor: primaryDisabled ? 'not-allowed' : 'pointer',
    opacity: primaryDisabled ? 0.78 : 1,
    boxShadow: primaryDisabled ? 'none' : '0 2px 10px var(--color-shadow)',
    boxSizing: 'border-box',
  };

  const primaryBarBtnNarrow = {
    ...primaryBarBtn,
    width: '100%',
    flex: 'none',
    minHeight: lessonNarrow ? 56 : 48,
    padding: lessonNarrow ? '12px 14px' : '10px 12px',
    fontSize: lessonNarrow ? 15 : 14,
  };

  const handleNeedClue = () => {
    if (hintMax === 0) return;
    trackEvent('hint_requested', 'Learning', lessonId, {
      world_id: worldId,
      lesson_id: lessonId,
      step_index: stepIndex,
      source: 'shell_clue_button',
    });
    if (lessonPanel !== 'hint') {
      setLessonPanel('hint');
      setHintVisibleLevel(1);
      return;
    }
    if (hintVisibleLevel < hintMax) {
      setHintVisibleLevel((l) => l + 1);
    }
  };

  const handleWhyToggle = () => {
    if (lessonPanel === 'why') {
      setLessonPanel(null);
    } else {
      trackEvent('explanation_opened', 'Learning', lessonId, {
        world_id: worldId,
        lesson_id: lessonId,
        step_index: stepIndex,
        activity_type: step?.activityType ?? null,
      });
      setLessonPanel('why');
    }
  };

  const hintEntry =
    lessonPanel === 'hint' && hintVisibleLevel >= 1 && hintVisibleLevel <= hintMax
      ? displayHints[hintVisibleLevel - 1]
      : null;

  const floatingCardStyle = {
    borderRadius: 16,
    padding: '14px 16px',
    background: 'var(--color-bg-card)',
    border: '1px solid var(--color-border)',
    boxShadow: '0 8px 32px var(--color-shadow)',
    maxHeight: 'min(38vh, 280px)',
    overflowY: 'auto',
  };

  const showAnswerInShell =
    typeof lessonFooterActions?.showAnswer === 'function' && !interactiveComplete && hintMax > 0;

  const floatingShowAnswerBtn = {
    width: '100%',
    marginTop: 12,
    minHeight: 48,
    padding: '12px 14px',
    borderRadius: 14,
    border: 'none',
    background: 'var(--color-primary)',
    color: '#fff',
    fontWeight: 800,
    fontSize: 15,
    cursor: 'pointer',
    opacity: 1,
    boxShadow: '0 2px 12px var(--color-shadow)',
    boxSizing: 'border-box',
  };

  const floatingPanel =
    lessonPanel === 'hint' && hintEntry ? (
      <div className="hide-scrollbar" style={floatingCardStyle} role="dialog" aria-label="Hint">
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'space-between',
            gap: 10,
            marginBottom: 0,
          }}
        >
          <p style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 15, fontWeight: 700, color: 'var(--color-text)', lineHeight: 1.45 }}>
            {hintEntry.text}
          </p>
          {lessonNarrow ? (
            <button
              type="button"
              aria-label="Close hint"
              onClick={() => setLessonPanel(null)}
              style={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 12,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginTop: -4,
              }}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>
          ) : null}
        </div>
        {hintVisibleLevel < hintMax ? (
          <button
            type="button"
            style={{
              ...secondaryBarBtn,
              width: '100%',
              marginTop: 12,
              flex: 'unset',
            }}
            onClick={() => setHintVisibleLevel((l) => Math.min(hintMax, l + 1))}
          >
            More help →
          </button>
        ) : showAnswerInShell ? (
          <button
            type="button"
            style={floatingShowAnswerBtn}
            onClick={() => {
              setLessonPanel(null);
              lessonFooterActions.showAnswer();
            }}
            aria-label="Show the answer for this activity"
          >
            Show answer
          </button>
        ) : null}
        <p style={{ margin: '10px 0 0', fontSize: 12, opacity: 0.65 }}>
          {lessonNarrow ? 'Tap Close above or Continue when you are ready.' : 'Press Escape to close'}
        </p>
      </div>
    ) : lessonPanel === 'why' ? (
      <div className="hide-scrollbar" style={floatingCardStyle} role="dialog" aria-label="Why this step">
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 10 }}>
          <p style={{ margin: 0, flex: 1, minWidth: 0, fontSize: 15, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.5 }}>
            {whyText}
          </p>
          {lessonNarrow ? (
            <button
              type="button"
              aria-label="Close"
              onClick={() => setLessonPanel(null)}
              style={{
                flexShrink: 0,
                width: 40,
                height: 40,
                borderRadius: 12,
                border: '1px solid var(--color-border)',
                background: 'var(--color-bg)',
                color: 'var(--color-text)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                marginTop: -4,
              }}
            >
              <X size={20} strokeWidth={2.25} aria-hidden />
            </button>
          ) : null}
        </div>
        {step && isInteractiveStep(step) && typeof readableInstruction === 'string' && readableInstruction.trim() ? (
          <div style={{ marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--color-border)' }}>
            <p style={{ margin: '0 0 6px', fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', opacity: 0.72 }}>
              About this step
            </p>
            <p style={{ margin: 0, fontSize: 14, fontWeight: 500, color: 'var(--color-text)', lineHeight: 1.5, opacity: 0.92 }}>
              {readableInstruction.trim()}
            </p>
          </div>
        ) : null}
        <p style={{ margin: '10px 0 0', fontSize: 12, opacity: 0.65 }}>
          {lessonNarrow ? 'Tap Close above when you are done reading.' : 'Press Escape to close'}
        </p>
      </div>
    ) : null;

  // Stacked bar height reserve (shell footer pad + two control rows + optional status line) — keep in sync with styles above.
  let bottomBarStack = lessonNarrow ? 128 : 118;
  if (!showLessonNext) {
    bottomBarStack = lessonNarrow ? (activityFooterWrongHint ? 152 : 128) : activityFooterWrongHint ? 136 : 110;
  } else if (step && isInteractiveStep(step) && interactiveComplete) {
    bottomBarStack = lessonNarrow ? 148 : 134;
  }
  if (
    !interactiveComplete &&
    (isLessonMcEngine ||
      isLessonCanvasEngine ||
      isLessonPatternEngine ||
      isLessonTapSurfaceEngine ||
      isLessonSystemFlowEngine ||
      isLessonEvidenceDetectiveEngine)
  ) {
    bottomBarStack += 24;
  }

  /** Reserve space for floating hint/why above the footer (card uses max ~38vh + actions). */
  const floatingExtra =
    lessonPanel === 'hint' ? (lessonNarrow ? 268 : 216) : lessonPanel === 'why' ? (lessonNarrow ? 288 : 232) : 0;
  const mainPaddingBottom = `calc(${bottomBarStack + floatingExtra}px + env(safe-area-inset-bottom, 0px))`;
  const floatingBottomOffset = `calc(${Math.min(bottomBarStack, 220)}px + env(safe-area-inset-bottom, 0px))`;

  const bottomBar = (
    <div>
      {!showLessonNext && activityFooterWrongHint ? (
        <p style={footerHintLineStyle} role="status" aria-live="polite">
          {wrongFooterCopy}
        </p>
      ) : null}
      {showLessonNext && step && isInteractiveStep(step) && interactiveComplete && !suppressFooterSuccessStrip ? (
        <p style={footerSuccessLineStyle} role="status" aria-live="polite">
          {successFooterCopy}
        </p>
      ) : null}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          gap: 6,
          alignItems: 'stretch',
          marginTop:
            activityFooterWrongHint || (interactiveComplete && isInteractiveStep(step)) ? 4 : 0,
        }}
      >
        <button
          type="button"
          style={primaryBarBtnNarrow}
          disabled={primaryDisabled}
          onClick={primaryAction}
          aria-label={
            primaryLabel === 'Check'
              ? 'Check your answer for this activity'
              : typeof lessonFooterActions?.checkAnswer === 'function'
                ? `${primaryLabel} for this activity`
                : typeof lessonFooterActions?.continueLesson === 'function' && primaryLabel === 'Continue'
                  ? 'Continue to the next question'
                  : primaryDisabled
                    ? 'Complete the activity to continue'
                    : undefined
          }
        >
          {primaryLabel}
        </button>
        <div
          style={{
            display: 'flex',
            flexWrap: 'nowrap',
            gap: 6,
            alignItems: 'stretch',
            width: '100%',
            minWidth: 0,
          }}
        >
          <button
            type="button"
            style={{
              ...clueIconBtn,
              flex: lessonNarrow ? '0 0 48px' : '0 0 48px',
              minWidth: 48,
              maxWidth: 48,
              minHeight: 48,
              opacity: hintMax === 0 ? 0.48 : 1,
              cursor: hintMax === 0 ? 'not-allowed' : 'pointer',
            }}
            disabled={hintMax === 0}
            onClick={handleNeedClue}
            aria-label="Clue"
            title="Clue"
          >
            <HelpCircle size={lessonNarrow ? 22 : 22} strokeWidth={2} aria-hidden />
          </button>
          <button
            type="button"
            style={{
              ...secondaryBarBtn,
              flex: 1,
              minHeight: 48,
              padding: lessonNarrow ? '8px 6px' : '8px 8px',
              fontSize: lessonNarrow ? 12 : 13,
            }}
            onClick={handleWhyToggle}
          >
            Why?
          </button>
          {narrationInBottomBar ? (
            <LessonStepNarration
              src={stepNarration.src}
              audioRef={lessonAudioRef}
              stepId={step.stepId}
              stepTitle={step.title}
              autoplay={narrationAutoplay}
              variant="icon"
            />
          ) : null}
          {typeof lessonFooterActions?.showAnswer === 'function' && !interactiveComplete && hintMax === 0 ? (
            <button
              type="button"
              style={{
                ...secondaryBarBtn,
                flex: '1 1 0',
                minWidth: 0,
                minHeight: 48,
                padding: lessonNarrow ? '8px 4px' : '8px 6px',
                fontSize: lessonNarrow ? 12 : 13,
                borderStyle: 'dashed',
              }}
              onClick={() => {
                setLessonPanel(null);
                lessonFooterActions.showAnswer();
              }}
              aria-label="Show the answer for this activity"
            >
              Show answer
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );

  const metaLine = `${lesson.ageRange} · ~${minutes} min total`;
  const metaLineForShell = shellHeaderMinimal ? undefined : metaLine;

  /**
   * Narrow: outer column scrolls for non-interactive steps (recap/intro copy + footer narration),
   * and after an interactive step is done (avoids nested card scrollbar + swipe chaining mid-activity).
   */
  const shellUnlockScroll =
    lessonNarrow && step && (isInteractiveStep(step) ? interactiveComplete : true);

  return (
    <>
    <FocusedLessonShell
      backToWorld={backToWorld}
      worldTitle={worldTitle}
      lessonTitle={lesson.title}
      stepIndex={stepIndex}
      totalSteps={totalSteps}
      metaLine={metaLineForShell}
      headerMode={shellHeaderMinimal ? 'minimal' : 'default'}
      scrollMode={shellUnlockScroll ? 'lessonScroll' : 'locked'}
      mainPaddingBottom={mainPaddingBottom}
      floatingBottomOffset={floatingBottomOffset}
      floatingPanel={floatingPanel}
      bottomBar={bottomBar}
      headerAction={
        <button
          type="button"
          onClick={() => setReportOpen(true)}
          aria-label="Report an issue"
          style={{
            background: 'transparent',
            border: 'none',
            cursor: 'pointer',
            padding: '8px',
            color: 'var(--color-text-tertiary, rgba(0,0,0,0.35))',
            display: 'flex',
            alignItems: 'center',
          }}
        >
          <Flag size={18} aria-hidden />
        </button>
      }
    >
      <audio
        ref={lessonAudioRef}
        preload="metadata"
        style={{ position: 'absolute', width: 1, height: 1, opacity: 0, pointerEvents: 'none' }}
        aria-hidden
      />
      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={step?.stepId || stepIndex}
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -8 }}
          transition={{ duration: 0.22, ease: 'easeOut' }}
          style={lessonMainCardStyle}
        >
          <LessonActivityCard
            stepId={step?.stepId != null ? String(step.stepId) : ''}
            tapFirst={tapFirst || isLessonTapSurfaceEngine}
            mainScrollable={isInteractiveStepActive && (!shellUnlockScroll || !!lessonPanel)}
            header={
              <>
                {storyCharacterBar}
                {!(immersiveHeader && isLessonMcEngine) && !hideTapRoundShellTitle ? (
                  <h1
                    style={{
                      fontSize: titleSize,
                      fontWeight: 800,
                      margin: immersiveHeader ? (lessonNarrow ? '0 0 4px' : '0 0 6px') : '0 0 8px',
                      lineHeight: 1.22,
                      letterSpacing: '-0.02em',
                      color: 'var(--color-text)',
                    }}
                  >
                    {displayStepTitle}
                  </h1>
                ) : null}
                {immersiveHeader ? null : iconBlock}
                {!immersiveHeader && !(interactiveComplete && step && isInteractiveStep(step))
                  ? instructionBlock
                  : null}
              </>
            }
            main={
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  flex: 1,
                  minHeight: 0,
                  width: '100%',
                }}
              >
                {/* Fixed DOM order (narration then engine) so completing a step does not remount the engine
                    when immersiveHeader flips — remount was resetting canvas `placed` and repeating round 1. */}
                <div
                  style={{
                    order: immersiveHeader && lessonNarrow ? 2 : 1,
                    flexShrink: 0,
                    minHeight: 0,
                  }}
                >
                  {narrationBlock}
                </div>
                <div
                  style={{
                    order: immersiveHeader && lessonNarrow ? 1 : 2,
                    flex: 1,
                    minHeight: 0,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {showImmersiveInstruction ? (
                    <div
                      style={{
                        flexShrink: 0,
                        width: '100%',
                        marginBottom: lessonNarrow ? 8 : 10,
                      }}
                    >
                      {instructionBlock}
                    </div>
                  ) : null}
                  {engineBlock}
                  {step?.type === 'recap' && (
                    <QuickFeedback
                      type="lesson"
                      contentId={lessonId}
                      worldId={worldId}
                      character={storyCharacter || null}
                      totalCount={totalSteps}
                      completedFully={true}
                    />
                  )}
                </div>
              </div>
            }
          />
        </motion.div>
      </AnimatePresence>
    </FocusedLessonShell>
    <ReportModal
      isOpen={reportOpen}
      onClose={() => setReportOpen(false)}
      contentType="lesson"
      contentId={lessonId}
      contentTitle={lesson?.title || lessonId}
      character={storyCharacter || null}
      worldId={worldId}
      currentPosition={`step ${stepIndex + 1} of ${totalSteps}`}
    />
    </>
  );
}
